import {
  BookingLedgerRow,
  DeductionLedgerRow,
  VariationLedgerRow,
  type LedgerRowMenu,
} from '../../../shared/components/ledger';
import { POT_LEDGER_COLLAPSE, POT_LEDGER_COLUMNS } from './columns';
import { PotLedgerHoverCard, type PanelLink } from './PotLedgerHoverCard';
import type {
  PotDeductionEntry,
  PotLabourEntry,
  PotLedgerEntry,
  PotVariationEntry,
} from './toPotLedgerRows';

/** Every side panel a breakdown row can open. */
export interface PotLedgerRowActions {
  onOpenAllocation: (allocationId: string) => void;
  onOpenVariation: (
    variationId: string,
    siteId: number | null,
    plotId: number | null
  ) => void;
  onOpenDeduction: (deductionId: string) => void;
  onOpenSite: (siteId: number) => void;
  onOpenPlot: (plotId: number, siteId: number | null) => void;
  onOpenLabourItem: (
    labourItemId: number,
    siteId: number | null,
    plotId: number | null
  ) => void;
  onAdminCancel: (allocationId: string) => void;
}

export interface PotLedgerRowProps {
  entry: PotLedgerEntry;
  /** The pot's status allows an admin cancel. */
  canAdminCancel: boolean;
  /** The viewer holds the admin-cancel permission. */
  canCancelAllocations: boolean;
  actions: PotLedgerRowActions;
}

export const POT_LEDGER_ROW_TESTID = 'pot-ledger-row';

/**
 * The Site / Plot cell says where work happened but opens nothing, so the
 * site, plot and labour item panels are reached from the hover card.
 */
function panelLinks(
  entry: PotLedgerEntry,
  actions: PotLedgerRowActions
): PanelLink[] {
  const links: PanelLink[] = [];
  const { siteId, plotId } = entry;
  if (siteId != null) {
    links.push({ label: 'Site', onOpen: () => actions.onOpenSite(siteId) });
  }
  if (plotId != null) {
    links.push({
      label: 'Plot',
      onOpen: () => actions.onOpenPlot(plotId, siteId),
    });
  }
  if (entry.kind === 'labour' && entry.labourItemId != null) {
    const labourItemId = entry.labourItemId;
    links.push({
      label: 'Labour item',
      onOpen: () => actions.onOpenLabourItem(labourItemId, siteId, plotId),
    });
  }
  return links;
}

/**
 * Admin cancel is gated by the pot's status, not the row's — that is the rule
 * the pot screen has always used, and it differs from the site screen's
 * per-allocation rule on purpose.
 */
function cancelMenu(
  allocationId: string,
  canCancelAllocations: boolean,
  canAdminCancel: boolean,
  onAdminCancel: (allocationId: string) => void,
  testId: string
): LedgerRowMenu | undefined {
  if (!canCancelAllocations) {
    return undefined;
  }
  return {
    label: 'Cancel Allocation',
    disabled: !canAdminCancel,
    onSelect: () => onAdminCancel(allocationId),
    'data-testid': testId,
  };
}

function DeductionRow({
  entry,
  actions,
}: {
  entry: PotDeductionEntry;
  actions: PotLedgerRowActions;
}) {
  const open = () => actions.onOpenDeduction(entry.deductionId);
  return (
    <DeductionLedgerRow
      columns={POT_LEDGER_COLUMNS}
      collapse={POT_LEDGER_COLLAPSE}
      title={entry.title}
      status={entry.status}
      reference={entry.reference}
      createdBy={entry.who}
      createdAt={entry.when}
      amount={entry.amount}
      sitePlot={entry.sitePlot ?? undefined}
      onClick={open}
      hoverCard={
        <PotLedgerHoverCard
          entry={entry}
          onOpenDetails={open}
          links={panelLinks(entry, actions)}
        />
      }
      data-testid={POT_LEDGER_ROW_TESTID}
    />
  );
}

function LabourRow({
  entry,
  canAdminCancel,
  canCancelAllocations,
  actions,
}: { entry: PotLabourEntry } & Omit<PotLedgerRowProps, 'entry'>) {
  const open = () => actions.onOpenAllocation(entry.allocationId);
  return (
    <BookingLedgerRow
      columns={POT_LEDGER_COLUMNS}
      collapse={POT_LEDGER_COLLAPSE}
      title={entry.title}
      status={entry.status}
      reference={entry.reference}
      bookedBy={entry.who}
      bookedAt={entry.when}
      amount={entry.amount}
      sitePlot={entry.sitePlot ?? undefined}
      onClick={open}
      menu={cancelMenu(
        entry.allocationId,
        canCancelAllocations,
        canAdminCancel,
        actions.onAdminCancel,
        'pot-cancel-allocation'
      )}
      hoverCard={
        <PotLedgerHoverCard
          entry={entry}
          onOpenDetails={open}
          links={panelLinks(entry, actions)}
        />
      }
      data-testid={POT_LEDGER_ROW_TESTID}
    />
  );
}

function VariationRow({
  entry,
  canAdminCancel,
  canCancelAllocations,
  actions,
}: { entry: PotVariationEntry } & Omit<PotLedgerRowProps, 'entry'>) {
  // A variation allocation with no request id of its own is still reachable by
  // the allocation that carries it — the key the live screen falls back to.
  const open = () =>
    actions.onOpenVariation(
      entry.variationRequestId ?? entry.allocationId,
      entry.siteId,
      entry.plotId
    );
  return (
    <VariationLedgerRow
      columns={POT_LEDGER_COLUMNS}
      collapse={POT_LEDGER_COLLAPSE}
      workTypeName={entry.title}
      description={entry.description ?? undefined}
      status={entry.status}
      reference={entry.reference}
      bookedBy={entry.who}
      bookedAt={entry.when}
      amount={entry.amount}
      sitePlot={entry.sitePlot ?? undefined}
      onClick={open}
      menu={cancelMenu(
        entry.allocationId,
        canCancelAllocations,
        canAdminCancel,
        actions.onAdminCancel,
        'pot-cancel-variation'
      )}
      hoverCard={
        <PotLedgerHoverCard
          entry={entry}
          onOpenDetails={open}
          links={panelLinks(entry, actions)}
        />
      }
      data-testid={POT_LEDGER_ROW_TESTID}
    />
  );
}

/** Picks the ledger variant for one breakdown entry. */
export function PotLedgerRow({ entry, ...rest }: PotLedgerRowProps) {
  if (entry.kind === 'deduction') {
    return <DeductionRow entry={entry} actions={rest.actions} />;
  }
  if (entry.kind === 'variation') {
    return <VariationRow entry={entry} {...rest} />;
  }
  return <LabourRow entry={entry} {...rest} />;
}
